/**
 * PvEConfig — single visible toggle surface for the /pve URL.
 *
 * Sibling to `src/config/brawl/BrawlConfig.ts`. /brawl is the canonical
 * PvP test surface; /pve is the canonical PvE test surface — mob-driven
 * combat, no bots, friendly fire off, fast respawn for iterating on
 * ability VFX vs npcs.
 *
 * Wiring:
 *   - main.tsx              → reads on /pve path mount; injects URL params
 *                             + triggers cookie/storage wipe (mirrors /brawl)
 *   - PvEArena.ts           → recolored geometry clone of BrawlArena;
 *                             registers map id `pve-arena`
 *   - content/zones/pve_arena.toml → mob spawns. Zone id matches the map
 *                             id (`pve-arena`), so GameRoom's
 *                             `entitySpawner.spawnZoneMobs(loadedLevel.id)`
 *                             auto-populates the arena on join.
 *
 * Why mobs aren't listed here: the TOML pipeline is canonical for spawn
 * data (compile-content.ts → zones.gen.ts). Duplicating the roster in TS
 * would create drift. Edit the TOML, run `npm run compile:content`, push.
 *
 * See ~/.claude/projects/-home-dj-dev-helix-Helix2000/memory/feedback_brawl_url_canon.md
 * for the canon-test-surface pattern.
 */

export const PVE_CONFIG = {
  // ---------------------------------------------------------------------------
  // Cookie/cache hygiene — mirrors /brawl. Wipes Twitch session cookie +
  // helix_* localStorage on first /pve navigation per tab. Open a new tab
  // (or close+reopen) for a fresh wipe.
  // ---------------------------------------------------------------------------
  resetOnLoad: true,

  // ---------------------------------------------------------------------------
  // Class default — what DJ spawns into. Mage is the contrast pick: it has
  // the broadest VFX surface (projectile, AoE, beam, channel) and is the
  // class most often visually QA'd against mobs.
  // ---------------------------------------------------------------------------
  defaultClass: 'mage' as const,

  // ---------------------------------------------------------------------------
  // HUD overrides — what /pve wants visible.
  // - preset feeds devConfig's preset resolver. 'live-lobby' = clean
  //   social-hub viewport with all dev UI off, plus nameplates/HP bars on
  // - skipSplash/skipLogin auto-skip the click-through screens
  // ---------------------------------------------------------------------------
  preset: 'live-lobby' as const,
  skipSplash: true,
  skipLogin: true,
} as const;
